import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';

// Pages
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import CompanyDashboard from './pages/CompanyDashboard';
import WorkerDashboard from './pages/WorkerDashboard';
import WorkersPage from './pages/WorkersPage';
import WorkerDetailPage from './pages/WorkerDetailPage';
import CompanyDocumentsPage from './pages/CompanyDocumentsPage';
import WorkerDocumentsPage from './pages/WorkerDocumentsPage';
import CommunicationsPage from './pages/CommunicationsPage';
import CommunicationDetailPage from './pages/CommunicationDetailPage';
import TimeClockPage from './pages/TimeClockPage';
import TimeRecordsPage from './pages/TimeRecordsPage';
import SignableDocumentsCompanyPage from './pages/SignableDocumentsCompanyPage';
import SignableDocumentsWorkerPage from './pages/SignableDocumentsWorkerPage';
import TemplatesPage from './pages/TemplatesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import DailyReportPage from './pages/DailyReportPage';
import PortalSettingsPage from './pages/PortalSettingsPage';
import PublicPortalPage from './pages/PublicPortalPage';
import ComplaintsWorkerPage from './pages/ComplaintsWorkerPage';
import ComplaintsCompanyPage from './pages/ComplaintsCompanyPage';
import ChatPage from './pages/ChatPage';
import BatchWorkersPage from './pages/BatchWorkersPage';
import LandingPage from './pages/LandingPage';
import ContactRequestsPage from './pages/ContactRequestsPage';
import WorkReportsWorkerPage from './pages/WorkReportsWorkerPage';
import WorkReportsCompanyPage from './pages/WorkReportsCompanyPage';

import './App.css';

function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F4]">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'company') {
    return <Navigate to="/company" replace />;
  } else {
    return <Navigate to="/worker" replace />;
  }
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/empresa/:slug" element={<PublicPortalPage />} />

      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="companies" element={<AdminDashboard />} />
        <Route path="requests" element={<ContactRequestsPage />} />
      </Route>

      {/* Company routes */}
      <Route
        path="/company"
        element={
          <ProtectedRoute allowedRoles={['company']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CompanyDashboard />} />
        <Route path="workers" element={<WorkersPage />} />
        <Route path="workers/batch" element={<BatchWorkersPage />} />
        <Route path="workers/:workerId" element={<WorkerDetailPage />} />
        <Route path="documents" element={<CompanyDocumentsPage />} />
        <Route path="signable-documents" element={<SignableDocumentsCompanyPage />} />
        <Route path="communications" element={<CommunicationsPage />} />
        <Route path="communications/:commId" element={<CommunicationDetailPage />} />
        <Route path="time-records" element={<TimeRecordsPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="daily-report" element={<DailyReportPage />} />
        <Route path="portal-settings" element={<PortalSettingsPage />} />
        <Route path="work-reports" element={<WorkReportsCompanyPage />} />
      </Route>
      <Route
        path="/worker"
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<WorkerDashboard />} />
        <Route path="documents" element={<WorkerDocumentsPage />} />
        <Route path="signable-documents" element={<SignableDocumentsWorkerPage />} />
        <Route path="communications" element={<CommunicationsPage />} />
        <Route path="communications/:commId" element={<CommunicationDetailPage />} />
        <Route path="time-clock" element={<TimeClockPage />} />
        <Route path="work-reports" element={<WorkReportsWorkerPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
